/**
 * @fileoverview User Detail Page
 *
 * Purpose: Detailed admin view of a single user with management actions.
 * URL: /admin/users/$userId
 * Protection: Requires super_admin role
 *
 * Sections:
 * 1. Profile card (avatar, name, email, role, IDs, joined date)
 * 2. Organisation memberships table
 * 3. Subscription info
 * 4. Admin actions (Promote/Demote, Suspend/Unsuspend)
 *
 * Dependencies:
 * - Convex: admin.users.getById, updateSystemRole, setSuspended
 * - shadcn Card, Badge, Button, AlertDialog, Table
 */

import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { UserAvatar } from "@/ui/composed/user-avatar";
import { ArrowLeft, Shield, ShieldOff, Ban, CheckCircle2 } from "lucide-react";
import { useState } from "react";

/** Shape of an organisation membership in the user detail response */
interface UserOrg {
  _id: Id<"organizations">;
  name: string;
  role: string;
  joinedAt: number;
}
import { toast } from "sonner";

export const Route = createFileRoute("/_app/_auth/admin/_layout/users/$userId")(
  {
    beforeLoad: async ({ context }) => {
      const user = await context.queryClient.ensureQueryData(
        convexQuery(api.users.queries.getCurrentUser, {}),
      );
      if (!user?.isOwner) {
        throw redirect({ to: "/admin/master-library" });
      }
    },
    component: UserDetailPage,
  },
);

function UserDetailPage() {
  const { userId } = Route.useParams();

  const { data: user } = useQuery(
    convexQuery(api.admin.users.getById, {
      userId: userId as Id<"users">,
    }),
  );

  if (user === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">User not found.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Back link */}
      <Link
        to="/admin/users"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </Link>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle>User Profile</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <UserAvatar
              avatarUrl={user.avatarUrl}
              firstName={user.firstName}
              lastName={user.lastName}
              email={user.email}
              className="h-16 w-16"
              fallbackClassName="text-xl"
            />
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">
                {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                  user.email}
              </h2>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              <div className="flex gap-2">
                <Badge
                  variant={
                    user.systemRole === "super_admin" ? "default" : "secondary"
                  }
                >
                  {user.systemRole === "super_admin" ? "Super Admin" : "User"}
                </Badge>
                {user.suspended && (
                  <Badge variant="destructive">Suspended</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-3 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Auth ID:</span>{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {user.authId}
              </code>
            </div>
            {user.customerId && (
              <div>
                <span className="text-muted-foreground">Stripe ID:</span>{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {user.customerId}
                </code>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Joined:</span>{" "}
              {new Date(user._creationTime).toLocaleDateString()}
            </div>
            <div>
              <span className="text-muted-foreground">Jobs:</span>{" "}
              {user.jobCount}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Organisation Memberships */}
      <Card>
        <CardHeader>
          <CardTitle>Organisations ({user.organisations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {user.organisations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.organisations.map((org: UserOrg) => (
                  <TableRow key={org._id}>
                    <TableCell>
                      <Link
                        to="/admin/organisations/$orgId"
                        params={{ orgId: org._id }}
                        className="font-medium hover:underline"
                      >
                        {org.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{org.role}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(org.joinedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Not a member of any organisation.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          {user.subscription ? (
            <div className="grid gap-2 text-sm md:grid-cols-2">
              <div>
                <span className="text-muted-foreground">Plan:</span>{" "}
                {user.subscription.planName}
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>{" "}
                <Badge variant="outline">{user.subscription.status}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Interval:</span>{" "}
                {user.subscription.interval}
              </div>
              <div>
                <span className="text-muted-foreground">Period End:</span>{" "}
                {new Date(
                  user.subscription.currentPeriodEnd * 1000,
                ).toLocaleDateString()}
              </div>
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No active subscription.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Admin Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <RoleAction userId={user._id} currentRole={user.systemRole} />
          <SuspendAction
            userId={user._id}
            isSuspended={user.suspended ?? false}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role Action
// ---------------------------------------------------------------------------

function RoleAction({
  userId,
  currentRole,
}: {
  userId: Id<"users">;
  currentRole?: string;
}) {
  const updateRole = useMutation(api.admin.users.updateSystemRole);
  const [isUpdating, setIsUpdating] = useState(false);

  const isAdmin = currentRole === "super_admin";
  const newRole = isAdmin ? "user" : "super_admin";
  const actionLabel = isAdmin ? "Demote to User" : "Promote to Super Admin";
  const Icon = isAdmin ? ShieldOff : Shield;

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateRole({
        userId,
        systemRole: newRole as "super_admin" | "user",
      });
      toast.success(
        isAdmin
          ? "User demoted to regular user."
          : "User promoted to super admin.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update role.",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="gap-2" disabled={isUpdating}>
          <Icon className="h-4 w-4" />
          {actionLabel}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{actionLabel}</AlertDialogTitle>
          <AlertDialogDescription>
            {isAdmin
              ? "This user will lose access to the admin panel. Are you sure?"
              : "This user will gain full admin access to the platform. Are you sure?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleUpdate}>Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Suspend Action
// ---------------------------------------------------------------------------

function SuspendAction({
  userId,
  isSuspended,
}: {
  userId: Id<"users">;
  isSuspended: boolean;
}) {
  const setSuspended = useMutation(api.admin.users.setSuspended);
  const [isUpdating, setIsUpdating] = useState(false);

  const actionLabel = isSuspended ? "Unsuspend User" : "Suspend User";
  const Icon = isSuspended ? CheckCircle2 : Ban;

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await setSuspended({ userId, suspended: !isSuspended });
      toast.success(
        isSuspended ? "User has been unsuspended." : "User has been suspended.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update user.",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant={isSuspended ? "outline" : "destructive"}
          className="gap-2"
          disabled={isUpdating}
        >
          <Icon className="h-4 w-4" />
          {actionLabel}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{actionLabel}</AlertDialogTitle>
          <AlertDialogDescription>
            {isSuspended
              ? "This user will regain access to the platform. Are you sure?"
              : "This user will immediately lose access to the platform. All their active sessions will be invalidated. Are you sure?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleUpdate}
            className={
              isSuspended
                ? ""
                : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
            }
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
